import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLazyGetUserLevelsQuery } from "../../redux/userApi";
import "./UserTreeModal.css";

// ─── Colors per level ─────────────────────────────────────────────────────────
const LEVEL_COLORS = [
  { circle: "#6366f1", light: "#eef2ff", text: "#4338ca" }, // root – indigo
  { circle: "#10b981", light: "#ecfdf5", text: "#047857" }, // L1   – emerald
  { circle: "#3b82f6", light: "#eff6ff", text: "#1d4ed8" }, // L2   – blue
  { circle: "#f59e0b", light: "#fffbeb", text: "#b45309" }, // L3   – amber
  { circle: "#ef4444", light: "#fef2f2", text: "#b91c1c" }, // L4   – red
  { circle: "#8b5cf6", light: "#f5f3ff", text: "#6d28d9" }, // L5   – violet
  { circle: "#06b6d4", light: "#ecfeff", text: "#0e7490" }, // L6   – cyan
  { circle: "#f97316", light: "#fff7ed", text: "#c2410c" }, // L7   – orange
];

const getColor = (level) => LEVEL_COLORS[level % LEVEL_COLORS.length];

// ─── Node sizes per level ─────────────────────────────────────────────────────
const nodeSize = (level) => {
  if (level === 0) return { r: 46, fontSize: 13, subFont: 9  };
  if (level === 1) return { r: 38, fontSize: 12, subFont: 8.5};
  return                   { r: 28, fontSize: 10, subFont: 8  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// VERTICAL layout  (top → bottom)
// ═══════════════════════════════════════════════════════════════════════════════
const V_LEVEL_GAP = 185;  // vertical gap between levels (centre-to-centre)
const V_NODE_GAP  = 30;   // min horizontal gap between sibling node centres

function computeSubtreeWidth(node) {
  const { r } = nodeSize(node.level);
  const minW = Math.max(r * 2 + 40, 120);
  if (!node.children || node.children.length === 0) {
    node._leafW = minW;
    return minW;
  }
  let total = 0;
  node.children.forEach((c) => {
    const w = computeSubtreeWidth(c);
    total += w + V_NODE_GAP;
  });
  total -= V_NODE_GAP;
  node._leafW = Math.max(minW, total);
  return node._leafW;
}

function assignPositionsV(node, cx, depth) {
  node.x = cx;
  node.y = depth * V_LEVEL_GAP + nodeSize(node.level).r + 20;

  if (!node.children || node.children.length === 0) return;

  const totalW = node.children.reduce((s, c) => s + c._leafW + V_NODE_GAP, -V_NODE_GAP);
  let startX = cx - totalW / 2;
  node.children.forEach((c) => {
    assignPositionsV(c, startX + c._leafW / 2, depth + 1);
    startX += c._leafW + V_NODE_GAP;
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// HORIZONTAL layout  (left → right)
// ═══════════════════════════════════════════════════════════════════════════════
const H_LEVEL_GAP = 200;
const H_NODE_GAP  = 16;

function computeSubtreeHeight(node) {
  const { r } = nodeSize(node.level);
  const minH = r * 2;
  if (!node.children || node.children.length === 0) {
    node._leafH = minH;
    return minH;
  }
  let total = 0;
  node.children.forEach((c) => {
    const h = computeSubtreeHeight(c);
    total += h + H_NODE_GAP;
  });
  total -= H_NODE_GAP;
  node._leafH = Math.max(minH, total);
  return node._leafH;
}

function assignPositionsH(node, cy, depth) {
  node.y = cy;
  node.x = depth * H_LEVEL_GAP + nodeSize(node.level).r + 10;

  if (!node.children || node.children.length === 0) return;

  const totalH = node.children.reduce((s, c) => s + c._leafH + H_NODE_GAP, -H_NODE_GAP);
  let startY = cy - totalH / 2;
  node.children.forEach((c) => {
    assignPositionsH(c, startY + c._leafH / 2, depth + 1);
    startY += c._leafH + H_NODE_GAP;
  });
}

// ─── Collect all nodes & edges ────────────────────────────────────────────────
function collectNodes(node, nodes = [], edges = []) {
  nodes.push(node);
  if (node.children) {
    node.children.forEach((child) => {
      edges.push({ from: node, to: child });
      collectNodes(child, nodes, edges);
    });
  }
  return { nodes, edges };
}

// Helper to extract initials from name/email/id
const getInitials = (fname, lname, email, id) => {
  if (fname && lname) return (fname[0] + lname[0]).toUpperCase();
  if (fname) return fname.slice(0, 2).toUpperCase();
  if (lname) return lname.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return String(id || "").slice(0, 2).toUpperCase();
};

// ═══════════════════════════════════════════════════════════════════════════════
// SVG: Circular avatar node (vertical style)
// ═══════════════════════════════════════════════════════════════════════════════
const CircleNode = React.memo(({ node, onClick }) => {
  const color = getColor(node.level);
  const { r, fontSize, subFont } = nodeSize(node.level);

  const fullName = [node.fname, node.lname].filter(Boolean).join(" ");
  const displayName = fullName || (node.email ? node.email : `#${node.id}`);
  const shortName = displayName.length > 15 ? displayName.slice(0, 15) + "…" : displayName;

  const sponsorCode = node.SponsorCode || node.SponserCode || node.sponsor_code || node.sponsorCode || "";
  const displaySponsor = sponsorCode || node.email || "";
  const shortSponsor = displaySponsor.length > 15 ? displaySponsor.slice(0, 15) + "…" : displaySponsor;

  const initials = getInitials(node.fname, node.lname, node.email, node.id);

  // Text label block sits below the circle
  const labelY = r + 8;
  const labelBoxH = fontSize + subFont * 2 + 18;

  return (
    <g
      transform={`translate(${node.x}, ${node.y})`}
      onClick={() => onClick(node.id)}
      style={{ cursor: "pointer" }}
      className="tree-node-g"
    >
      {/* Glow ring */}
      <circle cx={0} cy={0} r={r + 4} fill={color.light} />
      {/* Main circle */}
      <circle cx={0} cy={0} r={r} fill={color.circle} />
      {/* Inner lighter circle for contrast */}
      <circle cx={0} cy={-r * 0.12} r={r * 0.55} fill="rgba(255,255,255,0.18)" />
      {/* Initials / Letter */}
      <text
        x={0} y={r * 0.15}
        textAnchor="middle" dominantBaseline="middle"
        fill="#fff" fontSize={r * 0.55} fontWeight="800"
        fontFamily="Inter, sans-serif"
        style={{ userSelect: "none" }}
      >
        {initials}
      </text>

      {/* Background card for text labels */}
      <rect
        x={-60}
        y={labelY - 2}
        width={120}
        height={labelBoxH}
        rx={6}
        fill="rgba(255, 255, 255, 0.92)"
        stroke={color.light}
        strokeWidth={1}
      />

      {/* Full Name */}
      <text
        x={0} y={labelY + fontSize}
        textAnchor="middle"
        fill={color.text} fontSize={fontSize} fontWeight="700"
        fontFamily="Inter, sans-serif"
      >
        {shortName}
      </text>
      {/* Sponsor Code */}
      <text
        x={0} y={labelY + fontSize + subFont + 4}
        textAnchor="middle"
        fill="#64748b" fontSize={subFont}
        fontFamily="Inter, sans-serif"
      >
        {shortSponsor}
      </text>
      {/* Level badge */}
      <text
        x={0} y={labelY + fontSize + subFont * 2 + 10}
        textAnchor="middle"
        fill={color.circle} fontSize={subFont - 0.5} fontWeight="600"
        fontFamily="Inter, sans-serif"
      >
        Level {node.level}
      </text>
    </g>
  );
});

// ═══════════════════════════════════════════════════════════════════════════════
// SVG: Compact card node (horizontal style – unchanged)
// ═══════════════════════════════════════════════════════════════════════════════
const NODE_W = 130;
const NODE_H = 56;

const CardNode = React.memo(({ node, onClick }) => {
  const color = getColor(node.level);

  const fullName = [node.fname, node.lname].filter(Boolean).join(" ");
  const displayName = fullName || (node.email ? node.email : `#${node.id}`);
  const shortName = displayName.length > 14 ? displayName.slice(0, 14) + "…" : displayName;

  const sponsorCode = node.SponsorCode || node.SponserCode || node.sponsor_code || node.sponsorCode || "";
  const displaySponsor = sponsorCode || node.email || "";
  const shortSponsor = displaySponsor.length > 16 ? displaySponsor.slice(0, 16) + "…" : displaySponsor;

  return (
    <g
      transform={`translate(${node.x - NODE_W / 2}, ${node.y - NODE_H / 2})`}
      onClick={() => onClick(node.id)}
      style={{ cursor: "pointer" }}
      className="tree-node-g"
    >
      <rect x={2} y={3} width={NODE_W} height={NODE_H} rx={10} ry={10} fill="rgba(0,0,0,0.07)" />
      <rect x={0} y={0} width={NODE_W} height={NODE_H} rx={10} ry={10} fill={color.light} stroke={color.circle} strokeWidth={1.8} />
      <rect x={0} y={0} width={36} height={20} rx={10} ry={10} fill={color.circle} />
      <rect x={10} y={0} width={26} height={20} fill={color.circle} />
      <text x={18} y={14} textAnchor="middle" fill="#fff" fontSize={10} fontWeight="700" fontFamily="Inter, sans-serif">L{node.level}</text>
      <text x={NODE_W / 2} y={26} textAnchor="middle" fill={color.text} fontSize={11} fontWeight="700" fontFamily="Inter, sans-serif">{shortName}</text>
      <text x={NODE_W / 2} y={41} textAnchor="middle" fill="#666" fontSize={9.5} fontFamily="Inter, sans-serif">{shortSponsor}</text>
      {node.children && node.children.length > 0 && (
        <text x={NODE_W - 9} y={NODE_H / 2 + 5} textAnchor="middle" fill={color.circle} fontSize={15} fontWeight="bold">›</text>
      )}
    </g>
  );
});

// ═══════════════════════════════════════════════════════════════════════════════
// SVG Edge – smooth Bézier, direction-aware, connecting circle centres
// ═══════════════════════════════════════════════════════════════════════════════
const Edge = ({ from, to, direction }) => {
  const color = getColor(from.level).circle;
  const fromR = nodeSize(from.level).r;
  const toR   = nodeSize(to.level).r;
  let d;

  if (direction === "vertical") {
    // bottom of parent node's text label area → top of child circle
    const { fontSize, subFont } = nodeSize(from.level);
    const labelBottom = fromR + fontSize + subFont * 2 + 18;
    const x1 = from.x;
    const y1 = from.y + labelBottom;
    const x2 = to.x;
    const y2 = to.y - toR;
    const my = (y1 + y2) / 2;
    d = `M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}`;
  } else {
    // right of parent → left of child (card nodes: use centre offset)
    const x1 = from.x + NODE_W / 2;
    const y1 = from.y;
    const x2 = to.x - NODE_W / 2;
    const y2 = to.y;
    const mx = (x1 + x2) / 2;
    d = `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
  }

  return (
    <path d={d} fill="none" stroke={color} strokeWidth={1.8}
      strokeOpacity={0.45} strokeDasharray={direction === "vertical" ? "none" : "none"} />
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Layout toggle
// ═══════════════════════════════════════════════════════════════════════════════
const LayoutToggle = ({ direction, onChange }) => (
  <div className="tree-layout-toggle">
    <button className={`tlt-btn ${direction === "vertical" ? "tlt-active" : ""}`}
      onClick={() => onChange("vertical")} title="Vertical (Top → Bottom)">
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="5" y="1" width="8" height="5" rx="2" fill="currentColor" />
        <line x1="9" y1="6" x2="9" y2="9" stroke="currentColor" strokeWidth="1.5" />
        <line x1="3" y1="9" x2="15" y2="9" stroke="currentColor" strokeWidth="1.5" />
        <rect x="1" y="9" width="6" height="5" rx="2" fill="currentColor" opacity="0.7" />
        <rect x="11" y="9" width="6" height="5" rx="2" fill="currentColor" opacity="0.7" />
      </svg>
      Vertical
    </button>
    <button className={`tlt-btn ${direction === "horizontal" ? "tlt-active" : ""}`}
      onClick={() => onChange("horizontal")} title="Horizontal (Left → Right)">
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="1" y="5" width="5" height="8" rx="2" fill="currentColor" />
        <line x1="6" y1="9" x2="9" y2="9" stroke="currentColor" strokeWidth="1.5" />
        <line x1="9" y1="3" x2="9" y2="15" stroke="currentColor" strokeWidth="1.5" />
        <rect x="9" y="1" width="5" height="6" rx="2" fill="currentColor" opacity="0.7" />
        <rect x="9" y="11" width="5" height="6" rx="2" fill="currentColor" opacity="0.7" />
      </svg>
      Horizontal
    </button>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Modal
// ═══════════════════════════════════════════════════════════════════════════════
const UserTreeModal = ({ show, onHide, userId }) => {
  const { t } = useTranslation();

  const [activeUserId, setActiveUserId] = useState(userId);
  const [history, setHistory]           = useState([]);
  const [direction, setDirection]       = useState("vertical");
  const [scale, setScale]               = useState(1);
  const [pan, setPan]                   = useState({ x: 0, y: 0 });

  const isPanning = useRef(false);
  const lastPan   = useRef({ x: 0, y: 0 });
  const canvasRef = useRef(null);

  const [fetchLevels, { data: apiResponse, isLoading, isFetching }] = useLazyGetUserLevelsQuery();

  // ── Reset on open ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (show && userId) {
      setActiveUserId(userId);
      setHistory([]);
      setScale(1);
      setPan({ x: 0, y: 0 });
    }
  }, [show, userId]);

  // ── Fetch ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (show && activeUserId) {
      fetchLevels({ user_id: activeUserId });
      setScale(1);
      setPan({ x: 0, y: 0 });
    }
  }, [show, activeUserId, fetchLevels]);

  const handleDirectionChange = (dir) => {
    setDirection(dir);
    setScale(1);
    setPan({ x: 0, y: 0 });
  };

  const handleNodeClick = useCallback((nodeId) => {
    if (nodeId !== activeUserId) {
      setHistory((p) => [...p, activeUserId]);
      setActiveUserId(nodeId);
    }
  }, [activeUserId]);

  const handleBack = () => {
    if (history.length > 0) {
      setActiveUserId(history[history.length - 1]);
      setHistory((p) => p.slice(0, -1));
    }
  };

  // ── Zoom ─────────────────────────────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setScale((s) => Math.min(3, Math.max(0.1, s - e.deltaY * 0.001)));
  }, []);

  // ── Pan ──────────────────────────────────────────────────────────────────────
  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    isPanning.current = true;
    lastPan.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseMove = (e) => {
    if (!isPanning.current) return;
    const dx = e.clientX - lastPan.current.x;
    const dy = e.clientY - lastPan.current.y;
    lastPan.current = { x: e.clientX, y: e.clientY };
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  };
  const stopPan = () => { isPanning.current = false; };

  // ── Build tree ───────────────────────────────────────────────────────────────
  const treeData = apiResponse?.data || {};

  const rootNodes = useMemo(() => {
    const allUsers = Object.keys(treeData)
      .filter((k) => k.startsWith("level"))
      .flatMap((k) => treeData[k] || []);
    if (!allUsers.length) return [];

    const userMap = {};
    allUsers.forEach((u) => { userMap[u.id] = { ...u, children: [] }; });

    const roots = [];
    allUsers.forEach((u) => {
      if (u.level === 0)                                  roots.push(userMap[u.id]);
      else if (u.refer_user_id && userMap[u.refer_user_id]) userMap[u.refer_user_id].children.push(userMap[u.id]);
      else                                                roots.push(userMap[u.id]);
    });
    return roots;
  }, [treeData]);

  // ── Layout ───────────────────────────────────────────────────────────────────
  const { nodes, edges, totalW, totalH, initPan } = useMemo(() => {
    if (!rootNodes.length) return { nodes: [], edges: [], totalW: 0, totalH: 0, initPan: { x: 0, y: 0 } };

    const cloneTree = (n) => ({ ...n, children: (n.children || []).map(cloneTree) });

    let root;
    if (rootNodes.length === 1) {
      root = cloneTree(rootNodes[0]);
    } else {
      root = { id: "__virtual__", level: -1, email: "", children: rootNodes.map(cloneTree) };
    }

    if (direction === "vertical") {
      computeSubtreeWidth(root);
      const totalTreeW = root._leafW;
      assignPositionsV(root, totalTreeW / 2, 0);
    } else {
      computeSubtreeHeight(root);
      const totalTreeH = root._leafH;
      assignPositionsH(root, totalTreeH / 2, 0);
    }

    const { nodes: ns, edges: es } = collectNodes(root);
    const realNodes = rootNodes.length === 1 ? ns : ns.filter((n) => n.id !== "__virtual__");
    const realEdges = rootNodes.length === 1 ? es : es.filter((e) => e.from.id !== "__virtual__");

    const xs = realNodes.map((n) => n.x);
    const ys = realNodes.map((n) => n.y);
    const minX = Math.min(...xs) - 60;
    const minY = Math.min(...ys) - 60;
    const maxX = Math.max(...xs) + 100;
    const maxY = Math.max(...ys) + 100;

    return {
      nodes: realNodes,
      edges: realEdges,
      totalW: maxX - minX,
      totalH: maxY - minY,
      initPan: { x: -minX, y: -minY },
    };
  }, [rootNodes, direction]);

  // Apply initial pan offset once after layout
  useEffect(() => {
    setPan(initPan);
  }, [initPan]);

  const levelStats = useMemo(() => {
    const stats = {};
    nodes.forEach((n) => { if (n.level >= 0) stats[n.level] = (stats[n.level] || 0) + 1; });
    return stats;
  }, [nodes]);

  const isWorking = isLoading || isFetching;

  return (
    <Modal show={show} onHide={onHide} fullscreen={true} dialogClassName="tree-modal-dialog">
      <Modal.Header closeButton className="tree-modal-header">
        <div className="tree-header-content">
          <div className="tree-header-left">
            {history.length > 0 && (
              <button className="tree-back-btn" onClick={handleBack}>
                ← {t("back") || "Back"}
              </button>
            )}
            <span className="tree-title">{t("user_tree_levels") || "User Hierarchy Tree"}</span>
          </div>
          <div className="tree-header-right">
            <LayoutToggle direction={direction} onChange={handleDirectionChange} />
            <div className="tree-legend">
              {Object.entries(levelStats).sort(([a], [b]) => a - b).map(([lvl, cnt]) => {
                const color = getColor(Number(lvl));
                return (
                  <span key={lvl} className="tree-legend-item"
                    style={{ borderColor: color.circle, color: color.text, backgroundColor: color.light }}>
                    L{lvl}: {cnt}
                  </span>
                );
              })}
            </div>
            <div className="tree-zoom-controls">
              <button onClick={() => setScale((s) => Math.min(3, s + 0.15))}>＋</button>
              <span>{Math.round(scale * 100)}%</span>
              <button onClick={() => setScale((s) => Math.max(0.1, s - 0.15))}>－</button>
              <button onClick={() => { setScale(1); setPan(initPan); }} title="Reset view">⊙</button>
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="tree-modal-body p-0">
        {isWorking ? (
          <div className="tree-loader">
            <Spinner animation="border" variant="primary" />
            <p>Loading tree…</p>
          </div>
        ) : nodes.length === 0 ? (
          <div className="tree-empty">{t("no_record_found") || "No records found."}</div>
        ) : (
          <div
            ref={canvasRef}
            className="tree-canvas"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={stopPan}
            onMouseLeave={stopPan}
            onWheel={handleWheel}
          >
            <svg
              width={totalW} height={totalH}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                transformOrigin: "0 0",
                overflow: "visible",
              }}
            >
              {/* Edges behind nodes */}
              {edges.map((e, i) => (
                <Edge key={i} from={e.from} to={e.to} direction={direction} />
              ))}
              {/* Nodes */}
              {nodes.map((node) =>
                direction === "vertical"
                  ? <CircleNode key={node.id} node={node} onClick={handleNodeClick} />
                  : <CardNode   key={node.id} node={node} onClick={handleNodeClick} />
              )}
            </svg>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UserTreeModal;
